import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SharedOwnershipPage() {
  return (
    <div data-testid="shared-ownership-page">
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">Special Schemes</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Shared<br /><span className="text-[#d64241]">Ownership</span>
          </h1>
        </div>
      </section>

      <section data-testid="shared-ownership-content" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 font-body">
                If you can't afford all of the deposit and mortgage payments for a home that meets your needs, Shared Ownership offers you the chance to buy an initial share of a home worth between <strong className="text-black">10% and 75%</strong> of its market value. You will pay rent to the housing provider on the rest.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 font-body">
                You can buy more shares in your home in the future, as and when you can afford to do so. This is known as <strong className="text-black">'staircasing'</strong>. If you buy more shares, you'll pay less rent.
              </p>

              <h2 className="text-2xl md:text-3xl font-semibold text-black tracking-tight mb-4">New Model (April 2021)</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-4 font-body">
                In April 2021, the government launched a new model of Shared Ownership. As part of the new model:
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Introduced a 10-year period during which your housing provider will be required to support you with the cost of essential maintenance and repairs to your home",
                  "Lowered the size of the minimum share you can purchase through staircasing from 10% of a property's market value to 5%",
                  "Introduced a new form of staircasing that allows you to purchase an extra 1% of your home every year",
                  "Provided you with more control over when you sell your home",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#d64241] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-600 font-body leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#d64241] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-600 font-body leading-relaxed">
                    New model Shared Ownership homes have already come onto the market. However, old model Shared Ownership homes are still available. It is important to check which model you are applying for.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-semibold text-black tracking-tight mb-4">Am I eligible?</h2>
              <div className="space-y-3 mb-8">
                {[
                  "Your household earns £80,000 a year or less when you're buying outside of London, or £90,000 a year or less when you're buying in London.",
                  "You are a first-time buyer, you used to own a home but can't afford to buy one now, or are an existing shared owner looking to move.",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#d64241] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-600 font-body leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
              <p className="text-base text-gray-600 leading-relaxed mb-8 font-body">
                With Shared Ownership you can buy a newly built home or an existing one through resale programmes from housing associations. You'll need to take out a mortgage to pay for your share of the home's purchase price, or fund this through your savings. Shared Ownership properties are always leasehold.
              </p>

              <Link to="/contact">
                <Button data-testid="shared-ownership-cta-btn" className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200">
                  Get Expert Advice <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
                <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800&q=80" alt="Shared Ownership" className="w-full h-[400px] lg:h-[550px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
